// API Configuration
// This file centralizes all API endpoint configuration

// Determine if we're in development or production
// Check multiple conditions to be more robust
const isDevelopment = () => {
  // Check NODE_ENV first
  if (process.env.NODE_ENV === "development") {
    return true;
  }

  // Check hostname (localhost, 127.0.0.1, or any local IP)
  // If hostname is localhost or 127.0.0.1, we're definitely in development
  // regardless of port (could be 3000 for client, 4000 for server, etc.)
  if (typeof window !== "undefined" && window.location) {
    const hostname = window.location.hostname;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname.endsWith(".local")
    ) {
      return true;
    }
  }

  // Check if port indicates development (3000-3002 for React dev server, 4000 for API server)
  if (typeof window !== "undefined" && window.location) {
    const port = window.location.port;
    if (
      port === "3000" ||
      port === "3001" ||
      port === "3002" ||
      port === "4050"
    ) {
      return true;
    }
  }

  return false;
};

// Determine the API base URL based on environment
const getApiBaseUrl = () => {
  // In development, use localhost
  if (isDevelopment()) {
    return "http://localhost:4050";
  }

  // In production, use the API server domain
  // Server runs on: api-presentation.theautochef.com
  // Client runs on: presentation.theautochef.com
  return "https://api-presentation.theautochef.com";
};

// Export the API base URL
export const API_BASE_URL = getApiBaseUrl();

// Helper function to get full API URL
// API endpoints are directly accessible (no prefix needed)
export const getApiUrl = (endpoint) => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  return `${API_BASE_URL}/${cleanEndpoint}`;
};

// Helper function to get upload URL
// Files are served from /api/files endpoint to ensure requests reach Node.js
export const getUploadUrl = (filePath) => {
  if (!filePath) return "";
  // Remove leading slash if present
  const cleanPath = filePath.startsWith("/") ? filePath.slice(1) : filePath;
  return `${API_BASE_URL}/api/files/${cleanPath}`;
};

// Helper function to get viewer URL
export const getViewerUrl = (viewerToken) => {
  // Viewer is on the API domain, using /api/view to ensure requests reach Node.js
  const baseUrl = isDevelopment()
    ? "http://localhost:4050"
    : "https://api-presentation.theautochef.com";

  return `${baseUrl}/api/view/${viewerToken}`;
};

// Helper function to get SSE URL
// SSE is accessible via /api/sse to ensure requests reach Node.js
export const getSseUrl = (viewerToken) => {
  const baseUrl = isDevelopment()
    ? "http://localhost:4050"
    : "https://api-presentation.theautochef.com";

  const url = `${baseUrl}/api/sse/${viewerToken}`;

  if (isDevelopment()) {
    console.log("[getSseUrl] URL generation:", {
      isDevelopment: true,
      baseUrl,
      finalUrl: url,
      hostname:
        typeof window !== "undefined" ? window.location.hostname : "N/A",
      port: typeof window !== "undefined" ? window.location.port : "N/A",
    });
  }

  return url;
};

// Helper function to get PDF viewer URL
// PDF viewer is accessible via /api/pdf-viewer to ensure requests reach Node.js
export const getPdfViewerUrl = (filePath) => {
  const cleanPath = filePath.startsWith("/") ? filePath.slice(1) : filePath;
  return `${API_BASE_URL}/api/pdf-viewer?file=${encodeURIComponent(cleanPath)}`;
};

// Helper function to get PPTX viewer URL
// PPTX viewer is accessible via /api/pptx-viewer to ensure requests reach Node.js
export const getPptxViewerUrl = (filePath) => {
  const cleanPath = filePath.startsWith("/") ? filePath.slice(1) : filePath;
  return `${API_BASE_URL}/api/pptx-viewer?file=${encodeURIComponent(
    cleanPath
  )}`;
};
